import { Box, Button, ScrollArea, Stack, Text } from '@mantine/core';
import { IconHome, IconMessagePlus } from '@tabler/icons-react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useConversationsList } from '~portal/api';
import { AccountMenu } from '~portal/components/AccountMenu';
import { BrandLogo } from '~portal/components/BrandLogo';
import { ButtonLink } from '~portal/components/common';
import { AccountBlock } from '~portal/components/GlobalNav/AccountBlock';
import { Cmp, isPlatform, logger, trackAction } from '~portal/lib';
import { ChatListItem } from './ChatListItem';
import * as styles from './ChatSidebar.css';

export type ChatSidebarContentProps = {
  /** Called after navigation (e.g., to close drawer on mobile) */
  onItemClick?: () => void;
  /** Hide the New Chat button (rendered separately in drawer header) */
  hideNewChatButton?: boolean;
  /** Show the H2 Gambling Capital logo above the thread list (desktop sidebar only) */
  showLogo?: boolean;
  /**
   * Pin the account block to the bottom of the column. Both builds: the platform's global nav
   * is not rendered beside the chat, so this is the chat's only account block in either.
   */
  showAccount?: boolean;
};

/**
 * Platform only: the chat layout has no global nav (Figma `14:1450`), so this subtle link under
 * the logo is the way back to the rest of the platform. The standalone build is the chat alone
 * and has nowhere to go back to.
 */
const homeLink = (onClick?: () => void) =>
  isPlatform && (
    <ButtonLink
      to="/"
      onClick={onClick}
      variant="subtle"
      color="gray"
      size="compact-md"
      leftSection={<IconHome size={20} />}
      className={styles.homeLink}
    >
      Home
    </ButtonLink>
  );

/**
 * @desc Chat history content for reuse in both the desktop sidebar and the mobile Drawer.
 *
 * Charlie shape: a plain (borderless) column — logo, a flat list of conversation rows, and the
 * account block pinned to the bottom.
 */
export const ChatSidebarContent: Cmp<ChatSidebarContentProps> = function ({
  onItemClick,
  hideNewChatButton,
  showLogo,
  showAccount,
}) {
  const navigate = useNavigate();
  const conversations = useConversationsList();

  const handleNewChat = () => {
    trackAction('new_chat_started', { surface: 'sidebar' });
    navigate({ to: '/chat/$conversationId', params: { conversationId: 'new' } })
      .then(() => onItemClick?.())
      .catch((err) => {
        logger.error('[ChatSidebar] Navigation to new chat failed', {
          error: err instanceof Error ? err.message : String(err),
        });
      });
  };

  return (
    <Stack className={styles.sidebarContainer}>
      {showLogo && (
        <Box px="xs" className={styles.logoRow}>
          {/* In the platform the mark is the way home, as it is on every other page; the
              standalone build has no home to link to. */}
          {isPlatform ? (
            <Link to="/" aria-label="H2GC home" className={styles.logoLink}>
              <BrandLogo height={40} />
            </Link>
          ) : (
            <BrandLogo height={40} />
          )}
        </Box>
      )}

      {homeLink(onItemClick)}

      {/* New chat button - hidden in drawer where it's in the header */}
      {!hideNewChatButton && (
        <Button
          leftSection={<IconMessagePlus size={18} />}
          onClick={handleNewChat}
          variant="filled"
          radius="xl"
          fullWidth
          data-dd-action-name="New chat"
        >
          New Chat
        </Button>
      )}

      {/* Chat list */}
      <ScrollArea className={styles.scrollArea}>
        {conversations.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" mt="md">
            No conversations yet
          </Text>
        ) : (
          <Stack className={styles.chatList}>
            {conversations.map((conv) => (
              <ChatListItem
                key={conv._id}
                id={conv._id}
                updatedAt={conv.updatedAt.toISOString()}
                summary={conv.summary}
                onClick={onItemClick}
              />
            ))}
          </Stack>
        )}
      </ScrollArea>

      {/* Account block, pinned below the scrolling list — the list is the only part that
          scrolls away. */}
      {showAccount && (
        <Box className={styles.accountFooter}>
          {/* The platform draws the global nav's block (plan line, admin crown) so the account
              reads the same on every page; standalone keeps its organisation-name block. */}
          {isPlatform ? <AccountBlock /> : <AccountMenu placement="sidebar" />}
        </Box>
      )}
    </Stack>
  );
};

/**
 * @desc Desktop chat sidebar: a borderless column beside the conversation surface, carrying the
 * brand mark, the thread list and the account block. The chat sub-header on desktop is gone (the
 * Figma canvas is chromeless), so the sidebar carries the New Chat pill, mirroring the mobile
 * drawer. Brand mark and account block render in both builds: the platform's global nav, which
 * would otherwise carry them, stays out of the chat layout (Figma `14:1450`).
 */
export const ChatSidebar: Cmp = function () {
  return (
    <Box component="aside" py="lg" px="md" visibleFrom="md" className={styles.sidebar}>
      <ChatSidebarContent showLogo showAccount />
    </Box>
  );
};
